import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
} from '@angular/router';
import { ContainerSize } from '@freelancer/ui/container';
import { NavigationAlternate } from 'app/navigation/navigation.component';
import * as Rx from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

export interface LoggedInShellConfig {
  disableFooter?: boolean;
  disableMessaging?: boolean;
  /** hides messaging but leave admin chats open */
  adminOnlyMessaging?: boolean;
  disableChildNavigationActions?: boolean;
  disableNavigation?: boolean;
  disableNavigationChild?: boolean;
  disableToastNotifications?: boolean;
  disableVerificationBanner?: boolean;
  disablePostProjectButton?: boolean;
  enableMobileVerificationBanner?: boolean;
  containerSize?: ContainerSize;
  isMobilePrimaryView?: boolean;
  navigationAlternate?: NavigationAlternate;
  isLogoutPage?: boolean;
  fixedHeight?: boolean;
  /** Whether to bypass logout restricted for the given app/condition */
  bypassLoggedOutRestricted?: { [k in 'deloitte' | 'native']?: boolean };
  /** For pages that have a progress bar, e.g. onboarding */
  progress?: number;
}

export interface LoggedOutShellConfig {
  disableFooter?: boolean;
  disableNavigation?: boolean;
  containerSize?: ContainerSize;
  loginSignupModal?: boolean;
  disablePostProjectButton?: boolean;
  navigationAlternate?: NavigationAlternate;
  transparentNavigation?: boolean;
  /** Whether to bypass logout restricted for the given app/condition */
  bypassLoggedOutRestricted?: { [k in 'deloitte' | 'native']?: boolean };
}

/** Keep this in sync with ShellConfigTesting */
@Injectable({
  providedIn: 'root',
})
export class ShellConfig {
  constructor(private router: Router) {}

  getConfig<Config extends LoggedInShellConfig | LoggedOutShellConfig>(
    activatedRoute: ActivatedRoute,
  ): Rx.Observable<Config> {
    return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => activatedRoute.snapshot),
      startWith(activatedRoute.snapshot),
      map(snapshot => this.getRecursiveRouteData(snapshot)),
    );
  }

  /**
   * Get config from nested child routes.
   * Route data is specified in appropriate RoutingModules
   */
  getRecursiveRouteData<
    Config extends LoggedInShellConfig | LoggedOutShellConfig
  >(snapshot: ActivatedRouteSnapshot): Config {
    let { data } = snapshot;
    snapshot.children.forEach(child => {
      data = { ...data, ...this.getRecursiveRouteData(child) };
    });
    return data as Config;
  }
}
